import React, { useEffect, useState } from 'react';
import { validateFunctionalForm } from '../../utils';
import { v2_ships } from '../../actions/v2/projectAndVessel';
import { FaExchangeAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { v3_upload_document } from '../../actions/v3/document';
import { v2_document_type } from '../../actions/v2/masterData';
import constant from '../../constant';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { uploadSelectedFiles } from '../../utils/multipleFileUpload';

const Form = ({ data, onSubmit, projectId, encodedData, selectedShip, buttonName = 'Submit' }) => {
    const isEditMode = Boolean(data?.DOCUMENT_ID || data?.ID);
    const getInitialDocumentFiles = () => {
        if (!isEditMode) {
            return [{ fileName: '', originalFileName: '' }];
        }

        return (data?.files || []).map((file) => {
            const fileName = file.FILE_NAME || file.fileName || '';
            return {
                id: file.ID || file.id,
                fileName,
                originalFileName: file.ORIGINAL_FILE_NAME || file.originalFileName || fileName,
            };
        }).filter((file) => file.fileName);
    };

    const [state, setState] = useState({
        ships: { name: 'ships', value: data?.SHIP_ID || '', options: [], isRequired: true, error: '' },
        documentName: { name: 'documentName', value: data?.DOCUMENT_NAME || '', isRequired: true, error: '' },
        documentType: { name: 'documentType', value: data?.DOCUMENT_TYPE_ID || '', options: [], isRequired: true, error: '' },
        note: { name: 'note', value: data?.DESCRIPTION || '', isRequired: false, error: '' },
        documentFiles: {
            name: 'documentFiles',
            value: getInitialDocumentFiles(),
            isRequired: !isEditMode,
            error: ''
        },
    });

    useEffect(() => {
        v2_ships(null, projectId).then((res) => {
            if (res.status) {
                setState(prev => ({
                    ...prev,
                    ships: {
                        ...prev.ships,
                        options: res.result
                    }
                }));
            }
        });
    }, [projectId]);
    useEffect(() => {
        v2_document_type().then((res) => {
            if (res.status) {
                setState(prev => ({
                    ...prev,
                    documentType: {
                        ...prev.documentType,
                        options: res.result
                    }
                }));
            }
        });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                value,
                error: prev[name].isRequired && !value ? 'This field is required' : ''
            }
        }));
    };

    const handleFileChange = async (index, files) => {
        const uploadedFiles = await uploadSelectedFiles(files, v3_upload_document);
        if (!uploadedFiles.length) return;

        setState(prev => {
            const updatedFiles = [...prev.documentFiles.value];
            const currentFile = updatedFiles[index] || {};
            updatedFiles.splice(index, 1, ...uploadedFiles.map((file, fileIndex) => ({
                ...(fileIndex === 0 ? currentFile : {}),
                ...file
            })));

            return {
                ...prev,
                documentFiles: { ...prev.documentFiles, value: updatedFiles, error: '' }
            };
        });
    };

    const addFileField = () => {
        setState(prev => ({
            ...prev,
            documentFiles: {
                ...prev.documentFiles,
                value: [...prev.documentFiles.value, { fileName: '', originalFileName: '' }],
                error: ''
            }
        }));
    };

    const removeFileField = (index) => {
        const updatedFiles = state.documentFiles.value.filter((_, i) => i !== index);
        setState(prev => ({
            ...prev,
            documentFiles: {
                ...prev.documentFiles,
                value: updatedFiles,
                error: !isEditMode && updatedFiles.length === 0 ? 'At least one document is required' : ''
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateFunctionalForm(state, setState)) {
            const documentFiles = state.documentFiles.value.filter((file) => file?.fileName);

            if (!isEditMode && documentFiles.length === 0) {
                setState(prev => ({
                    ...prev,
                    documentFiles: {
                        ...prev.documentFiles,
                        error: 'At least one document is required'
                    }
                }));
                return;
            }

            const model = {
                ships: state.ships.value,
                documentName: state.documentName.value,
                note: state.note.value,
                documentType: state.documentType.value,
            };
            model.documentFiles = documentFiles;
            if (data?.ID) model.id = data.ID;
            onSubmit(model);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="row">
                <div className="col-md-4 mb-3">
                    <label>Hull Number {state.ships.isRequired && <span className='text-danger'> *</span>}</label>
                    <select
                        name={state.ships.name}
                        value={state.ships.value}
                        onChange={handleChange}
                        className={`form-control ${state.ships.error ? 'is-invalid' : ''}`}
                    >
                        <option value="">Select Hull Number</option>
                        {state.ships.options.map((ship) => (
                            <option key={ship.ID} value={ship.ID}>
                                {ship.HULL_NUMBER} - {ship.SHIP_NAME}
                            </option>
                        ))}
                    </select>
                    {state.ships.error && <div className="invalid-feedback">{state.ships.error}</div>}
                </div>

                <div className="col-md-4 mb-3">
                    <label>Document Name {state.documentName.isRequired && <span className='text-danger'> *</span>}</label>
                    <input
                        type="text"
                        name={state.documentName.name}
                        value={state.documentName.value}
                        onChange={handleChange}
                        className={`form-control ${state.documentName.error ? 'is-invalid' : ''}`}
                        placeholder="Document Name"
                    />
                    {state.documentName.error && <div className="invalid-feedback">{state.documentName.error}</div>}
                </div>
                <div className="col-md-4 mb-3">
                    <label>Document Tag {state.documentType.isRequired && <span className='text-danger'> *</span>}</label>
                    <select
                        name={state.documentType.name}
                        value={state.documentType.value}
                        onChange={handleChange}
                        className={`form-control ${state.documentType.error ? 'is-invalid' : ''}`}
                    >
                        <option value="">Select Tag</option>
                        {state.documentType.options.map((item) => (
                            <option key={item.ID} value={item.ID}>
                                {item.DOCUMENT_TYPE}
                            </option>
                        ))}
                    </select>
                    {state.documentType.error && <div className="invalid-feedback">{state.documentType.error}</div>}
                </div>

                <div className="col-md-12 mb-3">
                    <label>{isEditMode ? 'Files' : 'Upload Documents'}</label>

                    {state.documentFiles.value.map((item, index) => (
                        <div key={index} className="d-flex align-items-center mb-2">
                            {item.fileName ? (
                                <>
                                    <div className="me-2 flex-grow-1">
                                        <span>{item.originalFileName}</span>
                                    </div>
                                    <label className="btn btn-outline-secondary me-2 mb-0">
                                        <FaExchangeAlt className="me-1" /> Replace
                                        <input
                                            type="file"
                                            multiple
                                            onChange={(e) => handleFileChange(index, e.target.files)}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={() => removeFileField(index)}
                                    >
                                        <FaTrash />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={(e) => handleFileChange(index, e.target.files)}
                                        className="form-control me-2"
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={() => removeFileField(index)}
                                    >
                                        <FaTrash />
                                    </button>
                                </>
                            )}
                        </div>
                    ))}

                    <div>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={addFileField}
                        >
                            <FaPlus className="me-1" /> {isEditMode ? 'Add File' : 'Add Document'}
                        </button>
                    </div>

                    {state.documentFiles.error && (
                        <div className="text-danger mt-1">{state.documentFiles.error}</div>
                    )}
                </div>



                <div className="col-md-12 mb-3">
                    <label>Note</label>
                    <textarea
                        name={state.note.name}
                        value={state.note.value}
                        onChange={handleChange}
                        className="form-control"
                        rows="2"
                        placeholder="Any additional notes (optional)"
                    />
                </div>
            </div>

            <div className="mt-3">
                <button type="submit" className="btn btn-success me-2">{buttonName}</button>
                <Link className="btn btn-danger text-white" to={{
                    pathname: constant.Authedcomponent.shipDocument.url,
                    state: {
                        data: encodedData,
                        selectedShip
                    },
                }}>Cancel</Link>
            </div>
        </form>
    );
};

export default Form;
